import React, { useEffect, useMemo, useRef, useState } from "react";
import { PhoneCall, PhoneOff, Mic, MicOff, Volume2, VolumeX, RefreshCw } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";

function fmtTime(sec) {
  const m = String(Math.floor(sec / 60)).padStart(2, "0");
  const s = String(sec % 60).padStart(2, "0");
  return `${m}:${s}`;
}

function writeWav16kMono(samplesFloat32, sampleRate) {
  // Simple linear resampler to 16k (good enough for demo UI).
  const targetRate = 16000;
  const ratio = sampleRate / targetRate;
  const outLen = Math.max(1, Math.floor(samplesFloat32.length / ratio));
  const resampled = new Float32Array(outLen);
  for (let i = 0; i < outLen; i += 1) {
    const idx = i * ratio;
    const i0 = Math.floor(idx);
    const i1 = Math.min(samplesFloat32.length - 1, i0 + 1);
    const t = idx - i0;
    resampled[i] = samplesFloat32[i0] * (1 - t) + samplesFloat32[i1] * t;
  }

  const pcm16 = new Int16Array(resampled.length);
  for (let i = 0; i < resampled.length; i += 1) {
    const v = Math.max(-1, Math.min(1, resampled[i]));
    pcm16[i] = v < 0 ? v * 0x8000 : v * 0x7fff;
  }

  const bytesPerSample = 2;
  const blockAlign = 1 * bytesPerSample;
  const byteRate = targetRate * blockAlign;
  const dataSize = pcm16.length * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  const writeStr = (off, str) => { for (let i = 0; i < str.length; i += 1) view.setUint8(off + i, str.charCodeAt(i)); };
  writeStr(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, "WAVE");
  writeStr(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, targetRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true); // bits
  writeStr(36, "data");
  view.setUint32(40, dataSize, true);
  let o = 44;
  for (let i = 0; i < pcm16.length; i += 1, o += 2) view.setInt16(o, pcm16[i], true);
  return new Blob([buffer], { type: "audio/wav" });
}

export default function CallDemo() {
  const { t, lang } = useI18n();
  const [callState, setCallState] = useState("idle"); // idle | ringing | connected | ended
  const [seconds, setSeconds] = useState(0);
  const [sessionId, setSessionId] = useState(() => crypto.randomUUID());
  const [chatLang, setChatLang] = useState(lang);
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const [ttsOn, setTtsOn] = useState(true);
  const [messages, setMessages] = useState([]); // {role, content}
  const [err, setErr] = useState("");
  const scrollRef = useRef(null);
  const audioElRef = useRef(null);

  const audioCtxRef = useRef(null);
  const streamRef = useRef(null);
  const procRef = useRef(null);
  const chunksRef = useRef([]);
  const srRef = useRef(16000);

  const canSpeak = useMemo(() => typeof window !== "undefined" && "speechSynthesis" in window, []);

  useEffect(() => {
    // Only autoscroll when a new message arrives or "thinking" toggles.
    // Avoid forcing scroll during unrelated rerenders (e.g., timer ticks).
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length, busy]);

  useEffect(() => {
    if (callState !== "connected") return () => {};
    const timer = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, [callState]);

  const reset = () => {
    setErr("");
    setBusy(false);
    setListening(false);
    setCallState("idle");
    setSeconds(0);
    setMessages([]);
    setSessionId(crypto.randomUUID());
    try { window.speechSynthesis?.cancel?.(); } catch {}
    try { audioElRef.current?.pause?.(); audioElRef.current = null; } catch {}
  };

  const startCall = async () => {
    setErr("");
    setCallState("ringing");
    setSeconds(0);
    setMessages([]);
    setSessionId(crypto.randomUUID());
    setTimeout(async () => {
      setCallState("connected");
      setMessages([{ role: "assistant", content: t("call_demo.greeting") }]);
      if (ttsOn) {
        try {
          const r = await api.post("/tts/speak", { text: t("call_demo.greeting") }, { responseType: "blob" });
          const url = URL.createObjectURL(r.data);
          const a = new Audio(url);
          audioElRef.current = a;
          a.onended = () => URL.revokeObjectURL(url);
          await a.play();
        } catch {
          // Fallback to browser TTS if Azure isn't configured.
          if (canSpeak) {
            try { window.speechSynthesis.speak(new SpeechSynthesisUtterance(t("call_demo.greeting"))); } catch {}
          }
        }
      }
    }, 800);
  };

  const endCall = async () => {
    setCallState("ended");
    setListening(false);
    try { await stopListening(); } catch {}
    try { window.speechSynthesis?.cancel?.(); } catch {}
    try { audioElRef.current?.pause?.(); audioElRef.current = null; } catch {}
  };

  const stopListening = async () => {
    setListening(false);
    try {
      procRef.current?.disconnect?.();
      audioCtxRef.current?.close?.();
    } catch {}
    try { streamRef.current?.getTracks?.().forEach((t) => t.stop()); } catch {}
    procRef.current = null;
    audioCtxRef.current = null;
    streamRef.current = null;
  };

  const startListening = async () => {
    setErr("");
    if (busy || callState !== "connected") return;
    if (!navigator.mediaDevices?.getUserMedia) {
      setErr("Microphone not supported in this browser.");
      return;
    }

    chunksRef.current = [];
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    const ctx = new AudioContext();
    audioCtxRef.current = ctx;
    srRef.current = ctx.sampleRate;

    const source = ctx.createMediaStreamSource(stream);
    const proc = ctx.createScriptProcessor(4096, 1, 1);
    procRef.current = proc;
    source.connect(proc);
    proc.connect(ctx.destination);
    proc.onaudioprocess = (e) => {
      const data = e.inputBuffer.getChannelData(0);
      chunksRef.current.push(new Float32Array(data));
    };
    setListening(true);
  };

  const finishUtterance = async () => {
    if (!listening || busy) return;
    setListening(false);
    setBusy(true);
    setErr("");
    try {
      await stopListening();
      const total = chunksRef.current.reduce((sum, a) => sum + a.length, 0);
      const merged = new Float32Array(total);
      let off = 0;
      for (const a of chunksRef.current) { merged.set(a, off); off += a.length; }
      const wavBlob = writeWav16kMono(merged, srRef.current);

      // STT
      const fd = new FormData();
      fd.append("file", wavBlob, "speech.wav");
      const stt = await api.post("/stt/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const text = (stt.data?.text || "").trim();
      if (!text) {
        setErr(t("call_demo.no_speech"));
        return;
      }
      setMessages((m) => [...m, { role: "user", content: text }]);

      // LLM
      const r = await api.post("/ivr/chat", { session_id: sessionId, message: text, language: chatLang });
      const reply = r.data?.reply || "";
      setMessages((m) => [...m, { role: "assistant", content: reply }]);
      if (ttsOn && reply) {
        try {
          const r2 = await api.post("/tts/speak", { text: reply }, { responseType: "blob" });
          const url = URL.createObjectURL(r2.data);
          const a = new Audio(url);
          audioElRef.current = a;
          a.onended = () => URL.revokeObjectURL(url);
          await a.play();
        } catch {
          if (canSpeak) {
            try { window.speechSynthesis.speak(new SpeechSynthesisUtterance(reply)); } catch {}
          }
        }
      }
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
      chunksRef.current = [];
    }
  };

  return (
    <div className="space-y-6" data-testid="call-demo-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="chip"><PhoneCall className="h-3 w-3" /> {t("call_demo.title")}</div>
          <h1 className="mt-3 text-3xl lg:text-4xl font-extrabold text-white tracking-tight">{t("call_demo.title")}</h1>
          <p className="mt-1 text-slate-400 text-sm">{t("call_demo.sub")}</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 bg-bg-2 rounded-full p-1 border border-white/5">
            <button onClick={() => setChatLang("mn")} className={`px-3 py-1 text-xs font-bold rounded-full ${chatLang === "mn" ? "bg-primary text-white" : "text-slate-400"}`} data-testid="call-demo-lang-mn">МН</button>
            <button onClick={() => setChatLang("en")} className={`px-3 py-1 text-xs font-bold rounded-full ${chatLang === "en" ? "bg-primary text-white" : "text-slate-400"}`} data-testid="call-demo-lang-en">EN</button>
          </div>
          <button onClick={() => setTtsOn((v) => !v)} className="btn-ghost inline-flex items-center gap-2" data-testid="call-demo-tts">
            {ttsOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />} {t("call_demo.tts")}
          </button>
          <button onClick={reset} className="btn-ghost inline-flex items-center gap-2" data-testid="call-demo-reset">
            <RefreshCw className="h-4 w-4" /> {t("call_demo.reset")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4 glass rounded-3xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500">{t("call_demo.status")}</div>
              <div className="mt-1 text-white font-semibold" data-testid="call-demo-status">
                {callState === "idle" && t("call_demo.state_idle")}
                {callState === "ringing" && t("call_demo.state_ringing")}
                {callState === "connected" && t("call_demo.state_connected")}
                {callState === "ended" && t("call_demo.state_ended")}
              </div>
            </div>
            <div className="font-mono text-sm text-magenta" data-testid="call-demo-timer">{fmtTime(seconds)}</div>
          </div>

          {err && <div className="text-rose-400 text-sm">{err}</div>}

          <div className="flex flex-col gap-3">
            {callState === "idle" && (
              <button onClick={startCall} className="btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="call-demo-start">
                <PhoneCall className="h-4 w-4" /> {t("call_demo.start")}
              </button>
            )}
            {(callState === "ringing" || callState === "connected") && (
              <button onClick={endCall} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-rose-500/15 text-rose-200 border border-rose-500/20 hover:bg-rose-500/20 transition" data-testid="call-demo-end">
                <PhoneOff className="h-4 w-4" /> {t("call_demo.end")}
              </button>
            )}
          </div>

          <div className="pt-2 border-t border-white/5">
            <div className="text-xs uppercase tracking-widest text-slate-500">{t("call_demo.ptt")}</div>
            <div className="mt-3 flex gap-2">
              <button
                onMouseDown={startListening}
                onMouseUp={finishUtterance}
                onTouchStart={startListening}
                onTouchEnd={finishUtterance}
                disabled={busy || callState !== "connected"}
                className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border transition ${
                  listening ? "bg-magenta/15 border-magenta/30 text-magenta" : "bg-white/[0.03] border-white/10 text-slate-200 hover:bg-white/[0.06]"
                }`}
                data-testid="call-demo-ptt"
              >
                {listening ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
                {listening ? t("call_demo.listening") : t("call_demo.hold_to_talk")}
              </button>
            </div>
            <div className="mt-2 text-xs text-slate-500">{t("call_demo.ptt_hint")}</div>
          </div>
        </div>

        <div className="lg:col-span-8 glass rounded-3xl overflow-hidden flex flex-col" style={{ height: "calc(100vh - 240px)", minHeight: 420 }}>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 lg:p-10 space-y-4">
            {messages.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center text-center">
                <div className="h-20 w-20 rounded-full bg-gradient-to-br from-primary to-magenta shadow-glow-lg animate-orb-float flex items-center justify-center">
                  <PhoneCall className="h-8 w-8 text-white" />
                </div>
                <div className="mt-6 text-slate-400 max-w-md text-sm">{t("call_demo.hint")}</div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-4 py-3 rounded-2xl ${m.role === "user" ? "bg-bg-2 text-white rounded-tr-sm" : "bg-gradient-to-br from-primary to-primary-700 text-white rounded-tl-sm shadow-lg shadow-primary/30"}`}>
                  <div className="whitespace-pre-wrap leading-relaxed text-sm">{m.content}</div>
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="px-4 py-3 rounded-2xl rounded-tl-sm bg-gradient-to-br from-primary to-primary-700 text-white inline-flex items-center gap-3">
                  <div className="wave-bars"><span/><span/><span/><span/><span/></div>
                  <span className="text-xs">{t("demo.thinking")}…</span>
                </div>
              </div>
            )}
          </div>
          <div className="border-t border-white/5 p-4 text-xs text-slate-500">
            {t("call_demo.footer")}
          </div>
        </div>
      </div>
    </div>
  );
}

